// frontend/src/WorkoutTracker.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaPlus, FaTrash, FaSave } from "react-icons/fa";
import { apiFetch } from "./api";
import OyoWorkoutModal from "./OyoWorkoutModal";
import { formatPracticeTimeRange as basePracticeTimeRange } from "./utils";
const formatPracticeTimeRange = (practice) => basePracticeTimeRange(practice) || "All Day";

const DEFAULT_FORM = {
  name: "",
  workoutType: "erg",
  team: "",
  squadId: "",
  squadLabel: "",
  ageGroup: "",
  practiceId: "",
  measurementType: "distance",
  targetDistanceMeters: "",
  targetTime: "",
  pieceCount: "",
  notes: "",
};

const ALL_PRACTICES_OPTION = "ALL_PRACTICES";

function toLocalYmd(date) {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function parseDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const str = String(value).trim();
  if (!str) return null;
  const date = new Date(str);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatEntryDate(value) {
  const date = parseDateValue(value);
  if (!date) return "—";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatRelative(value) {
  const date = parseDateValue(value);
  if (!date) return "—";
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return `${Math.max(1, minutes)} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function normalizeOyoEntries(list = []) {
  return list.map((entry) => ({
    entryType: "oyo",
    id: entry.id || `oyo-${Math.random().toString(36).slice(2)}`,
    oyoId: entry.id || null,
    performedOn: entry.performedOn || entry.createdAt || null,
    description: entry.description || "OYO workout",
    workoutType: entry.workoutType || "erg",
    durationMinutes: entry.durationMinutes ?? null,
    distanceMeters: entry.distanceMeters ?? null,
    notes: entry.notes || "",
    createdAt: entry.createdAt || null,
  }));
}

function normalizeTeamResults(list = []) {
  return list.map((row) => ({
    entryType: "team",
    id:
      row.resultId ||
      `team-${row.workoutId || ""}-${row.pieceNumber || ""}-${Math.random()
        .toString(36)
        .slice(2)}`,
    performedOn: row.practiceDate || row.recordedAt || null,
    description: `${row.workoutName || "Workout"}${
      row.pieceNumber ? ` • Piece ${row.pieceNumber}` : ""
    }`,
    workoutType: row.workoutType || "erg",
    resultDistanceMeters: row.resultDistanceMeters ?? null,
    resultTimeSeconds: row.resultTimeSeconds ?? null,
    splitSeconds: row.splitSeconds ?? null,
    watts: row.watts ?? null,
    targetDistanceMeters: row.targetDistanceMeters ?? null,
    targetTimeSeconds: row.targetTimeSeconds ?? null,
    practiceTime: row.practiceTime || null,
    recordedAt: row.recordedAt || null,
    createdAt: row.recordedAt || null,
  }));
}

function sortHistoryEntries(list = []) {
  return [...list].sort((a, b) => {
    const aDate = parseDateValue(a?.performedOn) || parseDateValue(a?.createdAt);
    const bDate = parseDateValue(b?.performedOn) || parseDateValue(b?.createdAt);
    return (bDate ? bDate.getTime() : 0) - (aDate ? aDate.getTime() : 0);
  });
}

function formatTeamResult(entry) {
  const parts = [];
  if (entry.resultTimeSeconds != null) {
    parts.push(formatSeconds(entry.resultTimeSeconds));
  }
  if (entry.resultDistanceMeters != null) {
    parts.push(`${Math.round(entry.resultDistanceMeters)} m`);
  }
  return parts.length ? parts.join(" / ") : "—";
}


export function formatSeconds(totalSeconds) {
  if (totalSeconds == null || Number.isNaN(totalSeconds)) return "—";
  const seconds = Math.round(Number(totalSeconds));
  const absSeconds = Math.abs(seconds);
  const hrs = Math.floor(absSeconds / 3600);
  const mins = Math.floor((absSeconds % 3600) / 60);
  const secs = absSeconds % 60;
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function formatSecondsInput(totalSeconds) {
  if (totalSeconds == null || Number.isNaN(totalSeconds)) return "";
  return formatSeconds(totalSeconds);
}

function parseTimeInput(value) {
  if (value == null) return { seconds: null, error: null };
  const str = String(value).trim();
  if (!str) return { seconds: null, error: null };
  const parts = str.split(":").map((segment) => segment.trim());
  if (parts.length > 3) return { seconds: null, error: "Use HH:MM:SS or MM:SS" };
  let seconds = 0;
  const secPart = Number(parts.pop());
  if (Number.isNaN(secPart)) return { seconds: null, error: "Invalid seconds" };
  seconds += secPart;
  if (parts.length) {
    const minPart = Number(parts.pop());
    if (Number.isNaN(minPart)) return { seconds: null, error: "Invalid minutes" };
    seconds += minPart * 60;
  }
  if (parts.length) {
    const hourPart = Number(parts.pop());
    if (Number.isNaN(hourPart)) return { seconds: null, error: "Invalid hours" };
    seconds += hourPart * 3600;
  }
  return { seconds, error: null };
}

function formatPracticeLabel(practice) {
  if (!practice) return "Select a practice";
  const date = new Date(`${practice.date}T00:00:00`);
  const dateLabel = date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const timeLabel = formatPracticeTimeRange(practice);
  return `${dateLabel} • ${timeLabel}${
    practice.team ? ` • ${practice.team}` : ""
  }${practice.squadName ? ` • ${practice.squadName}` : ""}`;
}

function normalizeTeam(value) {
  if (!value) return "";
  return String(value).trim().toLowerCase();
}

function measurementForPiece(workout, piece) {
  if (!piece) return "distance";
  if (piece.targetDistanceMeters != null || workout.targetDistanceMeters != null) {
    return "distance";
  }
  return "time";
}

function resultKey(pieceId, athleteId) {
  return `${pieceId}:${athleteId}`;
}

const AVERAGES_TAB = "__AVERAGES__";
const AVAILABLE_ATTENDANCE = new Set(["present", "available"]);
const UNAVAILABLE_ATTENDANCE = new Set(["absent", "not available", "late", "excused"]);

function normalizeAttendanceStatus(status) {
  if (!status) return "";
  return String(status).trim().toLowerCase().replace(/_/g, " ");
}

function attendanceLabel(status) {
  const normalized = normalizeAttendanceStatus(status);
  if (!normalized) return "";
  if (normalized === "late") return "Late";
  if (normalized === "excused") return "Excused";
  if (normalized === "available") return "Available";
  if (normalized === "present") return "Present";
  if (normalized === "not available") return "Not Available";
  if (normalized === "absent") return "Absent";
  return normalized;
}

function attendanceIconMeta(status) {
  const normalized = normalizeAttendanceStatus(status);
  if (!normalized) return null;
  if (AVAILABLE_ATTENDANCE.has(normalized)) {
    return { icon: "✔", color: "#16a34a", label: attendanceLabel(normalized) };
  }
  if (UNAVAILABLE_ATTENDANCE.has(normalized)) {
    return { icon: "✕", color: "#dc2626", label: attendanceLabel(normalized) };
  }
  return { icon: "?", color: "#475569", label: attendanceLabel(normalized) || "No Response" };
}

function buildPracticeLabelFromMeta(meta) {
  if (!meta?.date) return null;
  return formatPracticeLabel({
    date: meta.date,
    startTime: meta.startTime || meta.start_time || meta.time || null,
    endTime: meta.endTime || meta.end_time || null,
    team: meta.team,
    squadName: meta.squadName || null,
  });
}

function determineWorkoutMeasurement(workout) {
  if (!workout) return "distance";
  if (workout.targetDistanceMeters != null) return "distance";
  if (workout.targetTimeSeconds != null) return "time";
  if (Array.isArray(workout.pieces) && workout.pieces.length) {
    const hasDistance = workout.pieces.some((p) => p?.targetDistanceMeters != null || p?.target_distance_m != null);
    if (hasDistance) return "distance";
    const hasTime = workout.pieces.some((p) => p?.targetTimeSeconds != null || p?.target_time_s != null);
    if (hasTime) return "time";
  }
  return "distance";
}

export default function WorkoutTracker({ coach, canEditWorkouts = false, focusRequest = null }) {
  const [practices, setPractices] = useState([]);
  const [loadingPractices, setLoadingPractices] = useState(false);
  const [practiceError, setPracticeError] = useState("");
  const [selectedPracticeId, setSelectedPracticeId] = useState("");
  const [viewMode, setViewMode] = useState("workouts");
  const [includeOyo, setIncludeOyo] = useState(false); // eslint-disable-line no-unused-vars
  const [attendanceByPractice, setAttendanceByPractice] = useState({});
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState("");
  const [attendanceFilter, setAttendanceFilter] = useState("all");

  const [workouts, setWorkouts] = useState([]);
  const [workoutsLoading, setWorkoutsLoading] = useState(false);
  const [workoutsError, setWorkoutsError] = useState("");

  const [createForm, setCreateForm] = useState(() => ({ ...DEFAULT_FORM }));
  const [creatingWorkout, setCreatingWorkout] = useState(false);

  const [roster, setRoster] = useState([]);
  const [rosterError, setRosterError] = useState("");

  const [squads, setSquads] = useState([]);
  const [teams, setTeams] = useState([]);

  const [resultInputs, setResultInputs] = useState({});
  const [savingPieces, setSavingPieces] = useState({});
  const [activePieceTabs, setActivePieceTabs] = useState({});
  const [expandedWorkouts, setExpandedWorkouts] = useState({});
  const [editingWorkouts, setEditingWorkouts] = useState({});
  const [pieceMutations, setPieceMutations] = useState({});
  const autoCommitSkipsRef = useRef({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [athleteOyoLatest, setAthleteOyoLatest] = useState({});
  const [individualAthleteId, setIndividualAthleteId] = useState("");
  const [individualEntries, setIndividualEntries] = useState([]);
  const [individualLoading, setIndividualLoading] = useState(false);
  const [individualError, setIndividualError] = useState("");
  const [oyoWorkoutModalId, setOyoWorkoutModalId] = useState(null);
  const practiceMap = useMemo(() => {
    const map = new Map();
    practices.forEach((practice) => {
      if (practice?.id) {
        map.set(String(practice.id), practice);
      }
    });
    return map;
  }, [practices]);

  useEffect(() => {
    if (!focusRequest) return;
    const { practiceId, action, workoutId } = focusRequest;
    if (practiceId) {
      const practiceIdStr = String(practiceId);
      setSelectedPracticeId(practiceIdStr);
      if (action === "create") {
        setShowCreateModal(true);
        setCreateForm((prev) => ({ ...prev, practiceId: practiceIdStr }));
      }
    }
    if (workoutId) {
      setExpandedWorkouts((prev) => ({
        ...prev,
        [workoutId]: true,
      }));
    }
  }, [focusRequest]);
  const startEditingWorkout = (workout) => {
    const pieces = workout.pieces || [];
    let measurementType = workout.targetDistanceMeters != null ? "distance" : null;
    if (!measurementType && workout.targetTimeSeconds != null) {
      measurementType = "time";
    }
    if (!measurementType && pieces.length) {
      measurementType = measurementForPiece(workout, pieces[0]);
    }
    if (!measurementType) measurementType = "distance";
    const initialDistance =
      measurementType === "distance"
        ? String(
            workout.targetDistanceMeters ??
              pieces[0]?.targetDistanceMeters ??
              ""
          )
        : "";
    const initialTime =
      measurementType === "time"
        ? formatSecondsInput(
            workout.targetTimeSeconds ?? pieces[0]?.targetTimeSeconds ?? ""
          )
        : "";
    setEditingWorkouts((prev) => ({
      ...prev,
      [workout.id]: {
        editing: true,
        saving: false,
        form: {
          name: workout.name || "",
          workoutType: workout.workoutType || "erg",
          team: workout.team || "",
          squadId: workout.squadId ? String(workout.squadId) : "",
          squadLabel: workout.squadLabel || "",
          ageGroup: workout.ageGroup || "",
          notes: workout.notes || "",
          measurementType,
          targetDistanceMeters: initialDistance,
          targetTime: initialTime,
        },
      },
    }));
  };

  const updateEditForm = (workoutId, updates) => {
    setEditingWorkouts((prev) => {
      const current = prev[workoutId];
      if (!current) return prev;
      return {
        ...prev,
        [workoutId]: {
          ...current,
          form: { ...current.form, ...updates },
        },
      };
    });
  };

  const changeEditSquad = (workoutId, value) => {
    const selected = squads.find((s) => String(s.id) === String(value));
    updateEditForm(workoutId, {
      squadId: value,
      squadLabel: selected?.name || "",
    });
  };

  const changeEditMeasurementType = (workoutId, measurementType) => {
    setEditingWorkouts((prev) => {
      const current = prev[workoutId];
      if (!current) return prev;
      const nextForm = { ...current.form, measurementType };
      if (measurementType === "distance" && !nextForm.targetDistanceMeters) {
        nextForm.targetDistanceMeters = "";
      }
      if (measurementType === "time" && !nextForm.targetTime) {
        nextForm.targetTime = "";
      }
      return {
        ...prev,
        [workoutId]: {
          ...current,
          form: nextForm,
        },
      };
    });
  };

  const setPieceMutationState = (workoutId, updates = {}) => {
    setPieceMutations((prev) => ({
      ...prev,
      [workoutId]: { ...(prev[workoutId] || {}), ...updates },
    }));
  };

  const clearPieceMutationState = (workoutId, fields) => {
    setPieceMutations((prev) => {
      if (!prev[workoutId]) return prev;
      const nextEntry = { ...prev[workoutId] };
      const list = Array.isArray(fields) ? fields : [fields];
      list.forEach((field) => delete nextEntry[field]);
      if (Object.keys(nextEntry).length === 0) {
        const next = { ...prev };
        delete next[workoutId];
        return next;
      }
      return { ...prev, [workoutId]: nextEntry };
    });
  };


  async function addPieceToWorkout(workout) {
    if (!allowEdits || !workout?.id) return;
    setPieceMutationState(workout.id, { adding: true });
    try {
      const response = await apiFetch(`/workouts/${workout.id}/pieces`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      const updatedWorkout = response?.workout || response;
      if (updatedWorkout) {
        updateWorkoutInState(updatedWorkout);
        const newPieceId =
          response?.pieceId ||
          (updatedWorkout.pieces?.length
            ? updatedWorkout.pieces[updatedWorkout.pieces.length - 1].id
            : null);
        if (newPieceId) {
          setActivePieceTabs((prev) => ({ ...prev, [workout.id]: newPieceId }));
        }
      } else if (selectedPracticeId) {
        await loadWorkouts(selectedPracticeId);
      }
    } catch (err) {
      alert(err?.message || "Failed to add piece");
    } finally {
      clearPieceMutationState(workout.id, "adding");
    }
  }

  async function deletePieceFromWorkout(workout, piece) {
    if (!allowEdits || !workout?.id || !piece?.id) return;
    if (!window.confirm("Delete this piece and its results?")) return;
    setPieceMutationState(workout.id, { deleting: piece.id });
    try {
      const updatedWorkout = await apiFetch(`/workout-pieces/${piece.id}`, {
        method: "DELETE",
      });
      if (updatedWorkout) {
        updateWorkoutInState(updatedWorkout);
        const nextPieces = updatedWorkout.pieces || [];
        setActivePieceTabs((prev) => {
          const next = { ...prev };
          if (!nextPieces.length) {
            delete next[workout.id];
            return next;
          }
          const prevSelection = prev[workout.id];
          if (!prevSelection || prevSelection === piece.id) {
            const fallbackIndex = Math.min(
              Math.max((piece.pieceNumber || 1) - 1, 0),
              nextPieces.length - 1
            );
            next[workout.id] = nextPieces[fallbackIndex]?.id;
          }
          return next;
        });
      } else if (selectedPracticeId) {
        await loadWorkouts(selectedPracticeId);
      }
    } catch (err) {
      alert(err?.message || "Failed to delete piece");
    } finally {
      clearPieceMutationState(workout.id, "deleting");
    }
  }

  const saveWorkoutDetails = async (workout) => {
    const state = editingWorkouts[workout.id];
    if (!state) return;
    const { form } = state;
    const measurementType = form.measurementType === "time" ? "time" : "distance";
    const trimmedName = form.name?.trim();
    if (!trimmedName) {
      alert("Workout name is required.");
      return;
    }

    const payload = {
      name: trimmedName,
      workoutType: form.workoutType || "erg",
      team: form.team || null,
      squadId: form.squadId || null,
      squadLabel: form.squadLabel || "",
      ageGroup: form.ageGroup ? form.ageGroup.trim() : null,
      notes: form.notes || "",
      measurementType,
    };

    if (payload.team === "") payload.team = null;
    if (payload.ageGroup === "") payload.ageGroup = null;
    if (payload.squadId != null && payload.squadId !== "") {
      const numericSquadId = Number(payload.squadId);
      if (!Number.isInteger(numericSquadId) || numericSquadId <= 0) {
        alert("Invalid squad selection.");
        return;
      }
      payload.squadId = numericSquadId;
    } else {
      payload.squadId = null;
      payload.squadLabel = "";
    }

    if (measurementType === "distance") {
      const distanceVal = Number(form.targetDistanceMeters);
      if (!Number.isFinite(distanceVal) || distanceVal <= 0) {
        alert("Enter a positive target distance.");
        return;
      }
      payload.targetDistanceMeters = Math.round(distanceVal);
    } else {
      const { seconds, error } = parseTimeInput(form.targetTime);
      if (error || !seconds) {
        alert(error || "Enter a valid target time.");
        return;
      }
      payload.targetTimeSeconds = Math.round(seconds);
    }

    setEditingWorkouts((prev) => ({
      ...prev,
      [workout.id]: {
        ...prev[workout.id],
        saving: true,
      },
    }));

    try {
      const updatedWorkout = await apiFetch(`/workouts/${workout.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      if (updatedWorkout) {
        setWorkouts((prev) =>
          prev.map((w) => (w.id === updatedWorkout.id ? updatedWorkout : w))
        );
      } else {
        await loadWorkouts(selectedPracticeId);
      }
      setEditingWorkouts((prev) => {
        const next = { ...prev };
        delete next[workout.id];
        return next;
      });
    } catch (err) {
      alert(err?.message || "Failed to update workout");
      setEditingWorkouts((prev) => {
        const current = prev[workout.id];
        if (!current) return prev;
        return {
          ...prev,
          [workout.id]: {
            ...current,
            saving: false,
          },
        };
      });
    }
  };

  const allowEdits = Boolean(canEditWorkouts);

  const selectedPractice =
    selectedPracticeId && selectedPracticeId !== ALL_PRACTICES_OPTION
      ? practiceMap.get(String(selectedPracticeId)) || null
      : null;
  const showingAllPractices = selectedPracticeId === ALL_PRACTICES_OPTION;

  const ageGroupOptions = useMemo(() => {
    const set = new Set();
    roster.forEach((athlete) => {
      if (athlete.age_group) {
        set.add(String(athlete.age_group).trim());
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [roster]);

  const loadPractices = useCallback(async () => {
    setLoadingPractices(true);
    setPracticeError("");
    try {
      const start = new Date();
      start.setDate(start.getDate() - 7);
      const end = new Date();
      end.setDate(end.getDate() + 21);
      const data = await apiFetch(`/practices/range?from=${toLocalYmd(start)}&to=${toLocalYmd(end)}`);
      const list = Array.isArray(data) ? data : [];
      setPractices(list);
      if (!selectedPracticeId && list.length) {
        setSelectedPracticeId(String(list[0].id));
      }
    } catch (e) {
      setPractices([]);
      setPracticeError(e?.message || "Failed to load practices");
    } finally {
      setLoadingPractices(false);
    }
  }, [selectedPracticeId]);

  const buildPracticeMeta = useCallback(
    (workout, explicitPractice = null) => {
      const practiceId =
        explicitPractice?.id ||
        workout.practiceId ||
        workout.practice_id ||
        null;
      const practiceInfo =
        explicitPractice ||
        (practiceId ? practiceMap.get(String(practiceId)) : null);
      const fallbackPractice =
        practiceInfo ||
        (workout.practiceDate ||
        workout.practiceTime ||
        workout.practiceStartTime
          ? {
              id: practiceId,
              date: workout.practiceDate || null,
              startTime:
                workout.practiceStartTime ||
                workout.practiceTime ||
                null,
              endTime: workout.practiceEndTime || null,
              team: workout.practiceTeam || null,
              squadName:
                workout.practiceSquad ||
                workout.squadLabel ||
                workout.squadName ||
                null,
            }
          : null);
      const practiceLabel =
        workout.practiceLabel ||
        buildPracticeLabelFromMeta(fallbackPractice);

      const practiceStartTime =
        fallbackPractice?.startTime ||
        fallbackPractice?.time ||
        workout.practiceStartTime ||
        workout.practiceTime ||
        null;
      const practiceEndTime =
        fallbackPractice?.endTime || workout.practiceEndTime || null;

      return {
        ...workout,
        practiceId: practiceId || null,
        practiceDate: fallbackPractice?.date || workout.practiceDate || null,
        practiceTime: practiceStartTime,
        practiceStartTime,
        practiceEndTime,
        practiceTeam: fallbackPractice?.team || workout.practiceTeam || null,
        practiceLabel: practiceLabel || null,
      };
    },
    [practiceMap]
  );

  const updateWorkoutInState = useCallback(
    (updatedWorkout) => {
      if (!updatedWorkout) return;
      const normalized = buildPracticeMeta(updatedWorkout);
      setWorkouts((prev) => prev.map((w) => (w.id === normalized.id ? normalized : w)));
    },
    [buildPracticeMeta]
  );

  const loadWorkouts = useCallback(
    async (practiceId) => {
      if (!practiceId) {
        setWorkouts([]);
        return;
      }
      setWorkoutsLoading(true);
      setWorkoutsError("");
      try {
        if (practiceId === ALL_PRACTICES_OPTION) {
          const practiceList = practices.filter((p) => p?.id);
          if (!practiceList.length) {
            setWorkouts([]);
            return;
          }
          const results = await Promise.all(
            practiceList.map(async (practice) => {
              try {
                const data = await apiFetch(`/practices/${practice.id}/workouts`);
                const list = Array.isArray(data) ? data : [];
                return list.map((workout) =>
                  buildPracticeMeta(workout, practice)
                );
              } catch (err) {
                console.warn(`Failed to load workouts for practice ${practice.id}`, err);
                return [];
              }
            })
          );
          const aggregated = results.flat().sort((a, b) => {
            const dateA = a.practiceDate || "";
            const dateB = b.practiceDate || "";
            if (dateA !== dateB) return dateA.localeCompare(dateB);
            const timeA = a.practiceTime || "";
            const timeB = b.practiceTime || "";
            if (timeA !== timeB) return timeA.localeCompare(timeB);
            return String(a.name || "").localeCompare(String(b.name || ""));
          });
          setWorkouts(aggregated);
          return;
        }

        const data = await apiFetch(`/practices/${practiceId}/workouts`);
        const practiceInfo = practiceMap.get(String(practiceId)) || null;
        const normalized = (Array.isArray(data) ? data : []).map((workout) =>
          buildPracticeMeta(workout, practiceInfo)
        );
        setWorkouts(normalized);
      } catch (e) {
        setWorkouts([]);
        setWorkoutsError(e?.message || "Failed to load workouts");
      } finally {
        setWorkoutsLoading(false);
      }
    },
    [practices, practiceMap, buildPracticeMeta]
  );

  const loadAttendanceForPractice = useCallback(async (practiceId) => {
    if (!practiceId || practiceId === ALL_PRACTICES_OPTION) {
      setAttendanceByPractice({});
      setAttendanceError("");
      setAttendanceLoading(false);
      return;
    }
    setAttendanceLoading(true);
    setAttendanceError("");
    try {
      const data = await apiFetch(`/attendance/by-practice/${practiceId}`);
      const map = {};
      (Array.isArray(data) ? data : []).forEach((row) => {
        const userId = row.userId || row.user_id || row.id;
        if (!userId) return;
        map[String(userId)] = normalizeAttendanceStatus(row.status);
      });
      setAttendanceByPractice((prev) => ({ ...prev, [String(practiceId)]: map }));
    } catch (e) {
      setAttendanceByPractice((prev) => {
        const next = { ...prev };
        delete next[String(practiceId)];
        return next;
      });
      setAttendanceError(e?.message || "Failed to load attendance");
    } finally {
      setAttendanceLoading(false);
    }
  }, []);

  const loadOyoSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (coach?.team) params.append("team", coach.team);
      const query = params.toString();
      const data = await apiFetch(
        `/oyo-workouts/latest${query ? `?${query}` : ""}`
      );
      const map = {};
      (Array.isArray(data) ? data : []).forEach((entry) => {
        if (!entry?.athleteId) return;
        map[String(entry.athleteId)] = entry;
      });
      setAthleteOyoLatest(map);
    } catch (e) {
      console.error("Failed to load OYO summary", e);
      setAthleteOyoLatest({});
    }
  }, [coach?.team]);

  const loadIndividualHistory = useCallback(
    async (athleteId) => {
      if (!athleteId) return;
      setIndividualLoading(true);
      setIndividualError("");
      try {
        const [teamData, oyoData] = await Promise.all([
          apiFetch(`/workout-results/mine?limit=50&as=${athleteId}`),
          apiFetch(`/oyo-workouts/mine?limit=50&as=${athleteId}`),
        ]);
        const combined = sortHistoryEntries([
          ...normalizeTeamResults(Array.isArray(teamData) ? teamData : []),
          ...normalizeOyoEntries(Array.isArray(oyoData) ? oyoData : []),
        ]);
        setIndividualEntries(combined);
      } catch (e) {
        console.error("Failed to load athlete history", e);
        setIndividualError(e?.message || "Failed to load athlete history");
        setIndividualEntries([]);
      } finally {
        setIndividualLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadPractices();
  }, [loadPractices]);

  useEffect(() => {
    if (selectedPracticeId) {
      loadWorkouts(selectedPracticeId);
    } else {
      setWorkouts([]);
    }
  }, [selectedPracticeId, loadWorkouts]);

  useEffect(() => {
    setAttendanceFilter("all");
    loadAttendanceForPractice(selectedPracticeId);
  }, [selectedPracticeId, loadAttendanceForPractice]);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch("/roster");
        const list = Array.isArray(data?.roster)
          ? data.roster
          : Array.isArray(data)
          ? data
          : [];
        setRoster(list);
      } catch (e) {
        setRoster([]);
        setRosterError(e?.message || "Failed to load roster");
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const squadData = await apiFetch("/squads");
        setSquads(Array.isArray(squadData) ? squadData : []);
      } catch {
        setSquads([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const teamData = await apiFetch("/teams");
        setTeams(Array.isArray(teamData) ? teamData : []);
      } catch {
        setTeams([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedPractice) return;
    setCreateForm((prev) => {
      const next = { ...prev };
      if (!prev.team && selectedPractice.team) {
        next.team = selectedPractice.team;
      }
      if (!prev.squadId && selectedPractice.squadId) {
        next.squadId = String(selectedPractice.squadId);
        next.squadLabel = selectedPractice.squadName || "";
      }
      return next;
    });
  }, [selectedPractice]);

  useEffect(() => {
    if (!coach?.team) return;
    setCreateForm((prev) => {
      if (prev.team) return prev;
      return { ...prev, team: coach.team };
    });
  }, [coach?.team]);

  useEffect(() => {
    setCreateForm((prev) => {
      const practiceExists = prev.practiceId
        ? practices.some((practice) => String(practice.id) === String(prev.practiceId))
        : false;
      const fallbackPracticeId =
        practiceExists
          ? prev.practiceId
          : selectedPracticeId && selectedPracticeId !== ALL_PRACTICES_OPTION
          ? String(selectedPracticeId)
          : practices.length
          ? String(practices[0].id)
          : "";
      if (fallbackPracticeId === (prev.practiceId || "")) {
        return prev;
      }
      return { ...prev, practiceId: fallbackPracticeId };
    });
  }, [practices, selectedPracticeId]);

  useEffect(() => {
    setResultInputs({});
    setActivePieceTabs({});
    setExpandedWorkouts({});
    setPieceMutations({});
  }, [selectedPracticeId]);

  useEffect(() => {
    if (viewMode === "athletes" && includeOyo) {
      loadOyoSummary();
    }
    if (!includeOyo) {
      setAthleteOyoLatest({});
    }
  }, [viewMode, includeOyo, loadOyoSummary]);

  useEffect(() => {
    if (viewMode !== "individual") return;
    if (!individualAthleteId) {
      setIndividualEntries([]);
      setIndividualError("");
      return;
    }
    loadIndividualHistory(individualAthleteId);
  }, [viewMode, individualAthleteId, loadIndividualHistory]);

  const practiceOptions = [
    <option key="all" value={ALL_PRACTICES_OPTION}>
      All Practices
    </option>,
    ...practices.map((practice) => (
      <option key={practice.id} value={practice.id}>
        {formatPracticeLabel(practice)}
      </option>
    )),
  ];

  function handleFormChange(field, value) {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleSquadChange(value) {
    const selected = squads.find((s) => String(s.id) === String(value));
    setCreateForm((prev) => ({
      ...prev,
      squadId: value,
      squadLabel: selected?.name || "",
    }));
  }

  async function handleCreateWorkout(e) {
    e.preventDefault();
    if (!allowEdits) return;
    const practiceId =
      createForm.practiceId && createForm.practiceId !== ALL_PRACTICES_OPTION
        ? String(createForm.practiceId)
        : "";
    if (!practiceId) {
      alert("Select a practice for this workout.");
      return;
    }

    const measurement = createForm.measurementType === "time" ? "time" : "distance";
    const body = {
      name: createForm.name?.trim() || "Workout",
      workoutType: createForm.workoutType || "erg",
      team: createForm.team || null,
      squadId: createForm.squadId || null,
      squadLabel: createForm.squadLabel || "",
      ageGroup: createForm.ageGroup || null,
      measurementType: measurement,
      pieceCount: Number(createForm.pieceCount) || 1,
      notes: createForm.notes || "",
    };

    if (measurement === "distance") {
      const distanceVal = Number(createForm.targetDistanceMeters);
      if (!Number.isFinite(distanceVal) || distanceVal <= 0) {
        alert("Enter a positive distance in meters.");
        return;
      }
      body.targetDistanceMeters = Math.round(distanceVal);
    } else {
      const { seconds, error } = parseTimeInput(createForm.targetTime);
      if (error || !seconds) {
        alert(error || "Enter a valid target time.");
        return;
      }
      body.targetTimeSeconds = Math.round(seconds);
    }

    setCreatingWorkout(true);
    try {
      const workout = await apiFetch(`/practices/${practiceId}/workouts`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (workout) {
        const normalized = buildPracticeMeta(workout, practiceMap.get(String(practiceId)) || null);
        if (
          selectedPracticeId === practiceId ||
          selectedPracticeId === ALL_PRACTICES_OPTION ||
          !selectedPracticeId
        ) {
          setWorkouts((prev) => [...prev, normalized]);
        }
        setCreateForm((prev) => ({
          ...DEFAULT_FORM,
          team: prev.team,
          squadId: prev.squadId,
          squadLabel: prev.squadLabel,
          practiceId: prev.practiceId,
        }));
        if (!selectedPracticeId || selectedPracticeId === ALL_PRACTICES_OPTION) {
          setSelectedPracticeId(practiceId);
        }
      } else {
        await loadWorkouts(practiceId);
      }
      setShowCreateModal(false);
    } catch (err) {
      alert(err?.message || "Failed to create workout");
    } finally {
      setCreatingWorkout(false);
    }
  }

  const getAthleteAttendanceStatus = (workout, athleteId) => {
    const practiceKey =
      workout?.practiceId || workout?.practice_id
        ? String(workout.practiceId || workout.practice_id)
        : "";
    if (!practiceKey) return "";
    const practiceAttendance = attendanceByPractice[practiceKey];
    if (!practiceAttendance) return "";
    return practiceAttendance[String(athleteId)] || "";
  };

  const passesAttendanceFilter = (workout, athleteId) => {
    if (!attendanceFilter || attendanceFilter === "all") return true;
    const practiceKey =
      workout?.practiceId || workout?.practice_id
        ? String(workout.practiceId || workout.practice_id)
        : "";
    if (!practiceKey) return true;
    const practiceAttendance = attendanceByPractice[practiceKey];
    if (!practiceAttendance) return true;
    const normalized = normalizeAttendanceStatus(
      practiceAttendance[String(athleteId)] || ""
    );
    if (!normalized) return attendanceFilter === "no_response";
    if (attendanceFilter === "available") return AVAILABLE_ATTENDANCE.has(normalized);
    if (attendanceFilter === "unavailable") return UNAVAILABLE_ATTENDANCE.has(normalized);
    return true;
  };

  function eligibleAthletes(workout) {
    return roster
      .filter((athlete) => {
        if (!athlete || athlete.role_key === "coach") return false;
        if (workout.team && normalizeTeam(athlete.team) !== normalizeTeam(workout.team)) {
          return false;
        }
        if (workout.squadId && Number(athlete.squadId) !== Number(workout.squadId)) {
          return false;
        }
        if (!workout.squadId && workout.squadLabel && workout.squadLabel.trim()) {
          const athleteSquad = athlete.squadName || athlete.squad || "";
          if (normalizeTeam(athleteSquad) !== normalizeTeam(workout.squadLabel)) {
            return false;
          }
        }
        if (workout.ageGroup) {
          const ag = athlete.age_group ? String(athlete.age_group).trim().toLowerCase() : "";
          if (ag !== String(workout.ageGroup).trim().toLowerCase()) return false;
        }
        if (!passesAttendanceFilter(workout, athlete.id)) return false;
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  function updatePieceInput(pieceId, athleteId, field, value) {
    const key = resultKey(pieceId, athleteId);
    setResultInputs((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
      },
    }));
  }

  function clearPieceInputs(pieceId, athleteId) {
    const key = resultKey(pieceId, athleteId);
    setResultInputs((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function markAutoCommitSkip(pieceId, athleteId) {
    const key = resultKey(pieceId, athleteId);
    autoCommitSkipsRef.current[key] = true;
  }

  function shouldSkipAutoCommit(pieceId, athleteId) {
    const key = resultKey(pieceId, athleteId);
    if (autoCommitSkipsRef.current[key]) {
      delete autoCommitSkipsRef.current[key];
      return true;
    }
    return false;
  }

  function getExistingResult(piece, athleteId) {
    if (!piece?.results?.length) return null;
    return piece.results.find((r) => Number(r.athleteId) === Number(athleteId)) || null;
  }

  function getInputValue(piece, athleteId, field, fallback) {
    const key = resultKey(piece.id, athleteId);
    if (Object.prototype.hasOwnProperty.call(resultInputs, key) && resultInputs[key][field] !== undefined) {
      return resultInputs[key][field];
    }
    return fallback;
  }

  async function savePieceResult(workout, piece, athlete, options = {}) {
    const { auto = false } = options;
    if (!allowEdits) return;
    const key = resultKey(piece.id, athlete.id);
    const inputs = resultInputs[key] || {};
    const measurement = measurementForPiece(workout, piece);
    const existing = getExistingResult(piece, athlete.id);
    const snapshot = { ...inputs };
    let resultValue = inputs.resultValue;
    if (!resultValue || !String(resultValue).trim()) {
      resultValue =
        measurement === "distance"
          ? formatSecondsInput(existing?.resultTimeSeconds)
          : existing?.resultDistanceMeters != null
          ? String(existing.resultDistanceMeters)
          : "";
    }
    if (!resultValue || !String(resultValue).trim()) {
      if (auto) return;
      alert("Enter a result before saving.");
      return;
    }
    const payload = {
      pieceId: piece.id,
      athleteId: athlete.id,
    };

    if (measurement === "distance") {
      const { seconds, error } = parseTimeInput(resultValue);
      if (error || seconds == null) {
        alert(error || "Enter a valid time (MM:SS).");
        return;
      }
      payload.resultTimeSeconds = Math.round(seconds);
    } else {
      const distance = Number(resultValue);
      if (!Number.isFinite(distance) || distance <= 0) {
        alert("Enter a positive distance in meters.");
        return;
      }
      payload.resultDistanceMeters = Math.round(distance);
    }

    if (Object.prototype.hasOwnProperty.call(inputs, "split")) {
      const splitValue = String(inputs.split || "").trim();
      if (splitValue) {
        const { seconds, error } = parseTimeInput(splitValue);
        if (error || seconds == null) {
          alert(error || "Split format should be MM:SS");
          return;
        }
        payload.splitSeconds = Number(seconds.toFixed(2));
      } else {
        payload.splitSeconds = null;
      }
    }

    if (Object.prototype.hasOwnProperty.call(inputs, "watts")) {
      const wattsValue = String(inputs.watts || "").trim();
      if (wattsValue) {
        const wattsVal = Number(wattsValue);
        if (!Number.isFinite(wattsVal) || wattsVal < 0) {
          alert("Watts must be a positive number.");
          return;
        }
        payload.watts = Math.round(wattsVal);
      } else {
        payload.watts = null;
      }
    }

    setSavingPieces((prev) => ({ ...prev, [piece.id]: true }));
    try {
      const updatedWorkout = await apiFetch(`/workouts/${workout.id}/results`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (updatedWorkout) {
        setWorkouts((prev) => prev.map((w) => (w.id === updatedWorkout.id ? updatedWorkout : w)));
        setResultInputs((prev) => {
          const current = prev[key];
          if (!current) return prev;
          const same =
            Object.keys(snapshot).length === Object.keys(current).length &&
            Object.entries(snapshot).every(([field, value]) => current[field] === value);
          if (!same) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      } else {
        await loadWorkouts(selectedPracticeId);
      }
    } catch (err) {
      alert(err?.message || "Failed to save workout result");
    } finally {
      setSavingPieces((prev) => {
        const next = { ...prev };
        delete next[piece.id];
        return next;
      });
    }
  }

  async function clearPieceResult(workout, piece, athlete) {
    if (!allowEdits) return;
    setSavingPieces((prev) => ({ ...prev, [piece.id]: true }));
    try {
      const updatedWorkout = await apiFetch(`/workouts/${workout.id}/results`, {
        method: "POST",
        body: JSON.stringify({
          pieceId: piece.id,
          athleteId: athlete.id,
        }),
      });
      if (updatedWorkout) {
        setWorkouts((prev) => prev.map((w) => (w.id === updatedWorkout.id ? updatedWorkout : w)));
        clearPieceInputs(piece.id, athlete.id);
      } else {
        await loadWorkouts(selectedPracticeId);
      }
    } catch (err) {
      alert(err?.message || "Failed to clear result");
    } finally {
      setSavingPieces((prev) => {
        const next = { ...prev };
        delete next[piece.id];
        return next;
      });
    }
  }

  function renderPieceResultsReadOnly(workout, piece) {
    if (!piece?.results?.length) {
      return <p className="help" style={{ marginTop: 8 }}>No results logged yet.</p>;
    }
    const measurement = measurementForPiece(workout, piece);
    return (
      <div style={{ overflowX: "auto", marginTop: 8 }}>
        <table className="table compact">
          <thead>
            <tr>
              <th>Athlete</th>
              <th>{measurement === "distance" ? "Time" : "Distance (m)"}</th>
              <th>Split</th>
              <th>Watts</th>
              <th>Logged By</th>
            </tr>
          </thead>
          <tbody>
            {piece.results.map((result) => (
              <tr key={result.id}>
                <td>{result.athleteName}</td>
                <td>
                  {measurement === "distance"
                    ? formatSeconds(result.resultTimeSeconds)
                    : result.resultDistanceMeters ?? "—"}
                </td>
                <td>{result.splitSeconds != null ? formatSeconds(result.splitSeconds) : "—"}</td>
                <td>{result.watts != null ? result.watts : "—"}</td>
                <td>{result.recordedByName || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function buildPieceRoster(workout, piece) {
    const eligible = eligibleAthletes(workout);
    const ids = new Set(eligible.map((a) => Number(a.id)));
    const extras = [];
    (piece.results || []).forEach((result) => {
      if (!ids.has(Number(result.athleteId)) && passesAttendanceFilter(workout, result.athleteId)) {
        ids.add(Number(result.athleteId));
        extras.push({
          id: result.athleteId,
          name: result.athleteName || `Athlete ${result.athleteId}`,
          role_key: "athlete",
        });
      }
    });
    return [...eligible, ...extras];
  }

function renderPieceGrid(workout, piece) {
  if (!piece) return null;
    const rows = buildPieceRoster(workout, piece);
    const measurement = measurementForPiece(workout, piece);
    const measurementLabel = measurement === "distance" ? "Result Time (MM:SS)" : "Distance (m)";

    if (!rows.length) {
      return <p className="help" style={{ marginTop: 8 }}>No eligible athletes for this workout.</p>;
    }

    return (
      <div style={{ overflowX: "auto", marginTop: 12 }}>
        <table className="table compact workout-entry-table">
          <thead>
            <tr>
              <th style={{ minWidth: 160 }}>Athlete</th>
              <th style={{ minWidth: 140 }}>{measurementLabel}</th>
              <th style={{ minWidth: 120 }}>Split (optional)</th>
              <th style={{ minWidth: 90 }}>Watts</th>
              <th style={{ minWidth: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((athlete) => {
              const existing = getExistingResult(piece, athlete.id);
              const defaultResult =
                measurement === "distance"
                  ? formatSecondsInput(existing?.resultTimeSeconds)
                  : existing?.resultDistanceMeters != null
                  ? String(existing.resultDistanceMeters)
                  : "";
              const defaultSplit =
                existing?.splitSeconds != null ? formatSecondsInput(existing.splitSeconds) : "";
              const defaultWatts =
                existing?.watts != null ? String(existing.watts) : "";

              const resultValue = getInputValue(piece, athlete.id, "resultValue", defaultResult);
              const splitValue = getInputValue(piece, athlete.id, "split", defaultSplit);
              const wattsValue = getInputValue(piece, athlete.id, "watts", defaultWatts);
              const attendanceStatus = getAthleteAttendanceStatus(workout, athlete.id);
              const attendanceMeta = attendanceIconMeta(attendanceStatus);

              return (
                <tr key={athlete.id}>
                  <td data-label="Athlete">
                    <div className="roster-name-row">
                      <span className="roster-name">{athlete.name}</span>
                      {attendanceMeta && (
                        <span
                          className="attendance-dot"
                          style={{ color: attendanceMeta.color, borderColor: attendanceMeta.color }}
                          title={attendanceMeta.label}
                        >
                          <span className="attendance-chip-icon">{attendanceMeta.icon}</span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td data-label={measurementLabel}>
                    <input
                      type="text"
                      className="input"
                      placeholder={measurement === "distance" ? "" : ""}
                      value={resultValue || ""}
                      onChange={(e) =>
                        updatePieceInput(piece.id, athlete.id, "resultValue", e.target.value)
                      }
                      onBlur={() => {
                        if (shouldSkipAutoCommit(piece.id, athlete.id)) return;
                        savePieceResult(workout, piece, athlete, { auto: true });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          markAutoCommitSkip(piece.id, athlete.id);
                          savePieceResult(workout, piece, athlete, { auto: true });
                        }
                      }}
                    />
                  </td>
                  <td data-label="Split (optional)">
                    <input
                      type="text"
                      className="input"
                      placeholder=""
                      value={splitValue || ""}
                      onChange={(e) => updatePieceInput(piece.id, athlete.id, "split", e.target.value)}
                      onBlur={() => {
                        if (shouldSkipAutoCommit(piece.id, athlete.id)) return;
                        savePieceResult(workout, piece, athlete, { auto: true });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          markAutoCommitSkip(piece.id, athlete.id);
                          savePieceResult(workout, piece, athlete, { auto: true });
                        }
                      }}
                    />
                  </td>
                  <td data-label="Watts">
                    <input
                      type="number"
                      className="input"
                      placeholder=""
                      value={wattsValue || ""}
                      onChange={(e) => updatePieceInput(piece.id, athlete.id, "watts", e.target.value)}
                      onBlur={() => {
                        if (shouldSkipAutoCommit(piece.id, athlete.id)) return;
                        savePieceResult(workout, piece, athlete, { auto: true });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          markAutoCommitSkip(piece.id, athlete.id);
                          savePieceResult(workout, piece, athlete, { auto: true });
                        }
                      }}
                    />
                  </td>
                  <td data-label="Actions" className="workout-entry-actions">
                    <button
                      type="button"
                      className="btn ghost tiny"
                      disabled={savingPieces[piece.id]}
                      onClick={() => clearPieceResult(workout, piece, athlete)}
                    >
                      <FaTrash /> Clear
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {savingPieces[piece.id] && (
          <p className="help" style={{ marginTop: 8 }}>Saving results…</p>
        )}
      </div>
    );
}

  const computeAthleteAverages = useCallback((workout) => {
    const rosterMap = new Map(
      (Array.isArray(roster) ? roster : []).map((athlete) => [Number(athlete.id), athlete])
    );

    const summary = new Map();

    (workout.pieces || []).forEach((piece) => {
      const measurement = measurementForPiece(workout, piece);
      (piece.results || []).forEach((result) => {
        const athleteId = Number(result.athleteId);
        if (!athleteId) return;
        if (!summary.has(athleteId)) {
          const rosterEntry = rosterMap.get(athleteId);
          summary.set(athleteId, {
            athleteId,
            name: rosterEntry?.name || result.athleteName || `Athlete ${athleteId}`,
            timeSum: 0,
            timeCount: 0,
            distanceSum: 0,
            distanceCount: 0,
            splitSum: 0,
            splitCount: 0,
            wattsSum: 0,
            wattsCount: 0,
          });
        }
        const entry = summary.get(athleteId);
        if (measurement === "distance") {
          if (typeof result.resultTimeSeconds === "number" && !Number.isNaN(result.resultTimeSeconds)) {
            entry.timeSum += result.resultTimeSeconds;
            entry.timeCount += 1;
          }
        } else {
          if (typeof result.resultDistanceMeters === "number" && !Number.isNaN(result.resultDistanceMeters)) {
            entry.distanceSum += result.resultDistanceMeters;
            entry.distanceCount += 1;
          }
        }
        if (typeof result.splitSeconds === "number" && !Number.isNaN(result.splitSeconds)) {
          entry.splitSum += result.splitSeconds;
          entry.splitCount += 1;
        }
        if (typeof result.watts === "number" && !Number.isNaN(result.watts)) {
          entry.wattsSum += result.watts;
          entry.wattsCount += 1;
        }
      });
    });

    const rows = Array.from(summary.values())
      .map((entry) => ({
        athleteId: entry.athleteId,
        name: entry.name,
        avgTimeSeconds: entry.timeCount ? entry.timeSum / entry.timeCount : null,
        avgDistanceMeters: entry.distanceCount ? entry.distanceSum / entry.distanceCount : null,
        avgSplitSeconds: entry.splitCount ? entry.splitSum / entry.splitCount : null,
        avgWatts: entry.wattsCount ? entry.wattsSum / entry.wattsCount : null,
      }))
      .filter(
        (row) =>
          row.avgTimeSeconds != null ||
          row.avgDistanceMeters != null ||
          row.avgSplitSeconds != null ||
          row.avgWatts != null
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    return rows;
  }, [roster]);

  const athleteMatrix = useMemo(() => {
    if (!workouts.length) return { columns: [], rows: [] };
    const columns = [];
    workouts.forEach((workout) => {
      const stats = computeAthleteAverages(workout);
      if (!stats.length) return;
      const measurement = determineWorkoutMeasurement(workout);
      const dateLabel = workout.practiceDate
        ? new Date(`${workout.practiceDate}T00:00:00`).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
          })
        : null;
      const columnLabel = [dateLabel, workout.name || "Workout"]
        .filter(Boolean)
        .join(" • ");
      columns.push({
        id: String(workout.id),
        label: columnLabel || `Workout ${workout.id}`,
        measurement,
        stats,
      });
    });
    const rowsMap = new Map();
    columns.forEach((column) => {
      column.stats.forEach((stat) => {
        if (!rowsMap.has(stat.athleteId)) {
          rowsMap.set(stat.athleteId, {
            athleteId: stat.athleteId,
            name: stat.name,
            values: {},
          });
        }
        rowsMap.get(stat.athleteId).values[column.id] = stat;
      });
    });
    const rows = Array.from(rowsMap.values()).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    );
    return { columns, rows };
  }, [workouts, computeAthleteAverages]);

  function renderWorkoutAverages(workout) {
    const rows = computeAthleteAverages(workout);
    if (!rows.length) {
      return <p className="help" style={{ marginTop: 12 }}>No results logged yet.</p>;
    }

    const hasTime = rows.some((row) => row.avgTimeSeconds != null);
    const hasDistance = rows.some((row) => row.avgDistanceMeters != null);

    let resultHeader = "Avg Result";
    if (hasTime && !hasDistance) resultHeader = "Avg Time (MM:SS)";
    else if (!hasTime && hasDistance) resultHeader = "Avg Distance (m)";
    else if (hasTime && hasDistance) resultHeader = "Avg Time / Distance";

    return (
      <div style={{ overflowX: "auto", marginTop: 12 }}>
        <table className="table compact">
          <thead>
            <tr>
              <th style={{ minWidth: 160 }}>Athlete</th>
              <th style={{ minWidth: 140 }}>{resultHeader}</th>
              <th style={{ minWidth: 120 }}>Avg Split</th>
              <th style={{ minWidth: 90 }}>Avg Watts</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              let resultLabel = "—";
              if (row.avgTimeSeconds != null && (!hasDistance || !hasTime)) {
                resultLabel = formatSeconds(row.avgTimeSeconds);
              }
              if (row.avgDistanceMeters != null && !hasTime) {
                resultLabel = `${Math.round(row.avgDistanceMeters)} m`;
              } else if (row.avgDistanceMeters != null && hasTime && hasDistance) {
                const timeLabel =
                  row.avgTimeSeconds != null ? formatSeconds(row.avgTimeSeconds) : "—";
                resultLabel = `${timeLabel} / ${Math.round(row.avgDistanceMeters)} m`;
              } else if (row.avgTimeSeconds != null) {
                resultLabel = formatSeconds(row.avgTimeSeconds);
              }
              const splitLabel =
                row.avgSplitSeconds != null ? formatSeconds(row.avgSplitSeconds) : "—";
              const wattsLabel =
                row.avgWatts != null ? Math.round(row.avgWatts) : "—";
              return (
                <tr key={row.athleteId}>
                  <td>{row.name}</td>
                  <td>{resultLabel}</td>
                  <td>{splitLabel}</td>
                  <td>{wattsLabel}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  function renderWorkout(workout) {
    const measurement =
      workout.targetDistanceMeters != null
        ? "distance"
        : workout.targetTimeSeconds != null
        ? "time"
        : "distance";

    const pieces = workout.pieces || [];
    const activeKey = activePieceTabs[workout.id];
    const showingAverages = activeKey === AVERAGES_TAB;
    const defaultPieceId = pieces.length ? pieces[0].id : null;
    const activePieceId = showingAverages ? null : activeKey || defaultPieceId;
    const activePiece = pieces.find((p) => p.id === activePieceId) || null;

    const isExpanded = expandedWorkouts[workout.id] ?? false;
    const editingState = editingWorkouts[workout.id];
    const isEditing = Boolean(editingState?.editing);
    const isSavingEdit = Boolean(editingState?.saving);
    const editForm = editingState?.form || null;
    const mutationState = pieceMutations[workout.id] || {};
    const isAddingPiece = Boolean(mutationState.adding);
    const deletingPieceId = mutationState.deleting || null;

    return (
      <div key={workout.id} className="card" style={{ padding: 16, marginBottom: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            cursor: "pointer",
          }}
          onClick={() =>
            setExpandedWorkouts((prev) => ({ ...prev, [workout.id]: !isExpanded }))
          }
        >
          <div>
            <h3 style={{ margin: "0 0 4px" }}>{workout.name}</h3>
            <div style={{ fontSize: 13, color: "#475569" }}>
              {workout.workoutType?.toUpperCase()} • {workout.pieceCount} pieces •{" "}
              {measurement === "distance"
                ? `${workout.targetDistanceMeters ?? workout.pieces?.[0]?.targetDistanceMeters ?? 0}m`
                : formatSeconds(workout.targetTimeSeconds ?? workout.pieces?.[0]?.targetTimeSeconds)}
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: 12, color: "#475569" }}>
            {workout.team && <div>Team: {workout.team}</div>}
            {workout.squadLabel && <div>Squad: {workout.squadLabel}</div>}
            {workout.ageGroup && <div>Age: {workout.ageGroup}</div>}
          </div>
        </div>
        {showingAllPractices && workout.practiceLabel && (
          <div className="help" style={{ marginTop: 12 }}>
            Practice: {workout.practiceLabel}
          </div>
        )}

        {isExpanded && allowEdits && (
          <div style={{ marginTop: 12 }}>
            {isEditing && editForm ? (
              <div
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  padding: 12,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                  <label style={{ flex: "1 1 200px" }}>
                    <span className="label">Name</span>
                    <input
                      className="input"
                      value={editForm.name}
                      onChange={(e) => updateEditForm(workout.id, { name: e.target.value })}
                      disabled={isSavingEdit}
                    />
                  </label>
                  <label style={{ width: 160 }}>
                    <span className="label">Type</span>
                    <select
                      className="input"
                      value={editForm.workoutType}
                      onChange={(e) =>
                        updateEditForm(workout.id, { workoutType: e.target.value })
                      }
                      disabled={isSavingEdit}
                    >
                      <option value="erg">Erg</option>
                      <option value="water">Water</option>
                    </select>
                  </label>
                  <label style={{ width: 160 }}>
                    <span className="label">Team</span>
                    <select
                      className="input"
                      value={editForm.team}
                      onChange={(e) => updateEditForm(workout.id, { team: e.target.value })}
                      disabled={isSavingEdit}
                    >
                      <option value="">All Teams</option>
                      {teams.map((team) => (
                        <option key={team.id || team.name} value={team.name}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label style={{ width: 160 }}>
                    <span className="label">Squad</span>
                    <select
                      className="input"
                      value={editForm.squadId}
                      onChange={(e) => changeEditSquad(workout.id, e.target.value)}
                      disabled={isSavingEdit}
                    >
                      <option value="">All Squads</option>
                      {squads.map((squad) => (
                        <option key={squad.id} value={squad.id}>
                          {squad.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label style={{ width: 160 }}>
                    <span className="label">Age Group</span>
                    <select
                      className="input"
                      value={editForm.ageGroup}
                      onChange={(e) => updateEditForm(workout.id, { ageGroup: e.target.value })}
                      disabled={isSavingEdit}
                    >
                      <option value="">All</option>
                      {ageGroupOptions.map((group) => (
                        <option key={group} value={group}>
                          {group}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 12 }}>
                  <label style={{ width: 160 }}>
                    <span className="label">Measure By</span>
                    <select
                      className="input"
                      value={editForm.measurementType}
                      onChange={(e) => changeEditMeasurementType(workout.id, e.target.value)}
                      disabled={isSavingEdit}
                    >
                      <option value="distance">Distance</option>
                      <option value="time">Time</option>
                    </select>
                  </label>
                  {editForm.measurementType === "distance" ? (
                    <label style={{ width: 160 }}>
                      <span className="label">Distance (m)</span>
                      <input
                        type="number"
                        className="input"
                        value={editForm.targetDistanceMeters}
                        onChange={(e) =>
                          updateEditForm(workout.id, { targetDistanceMeters: e.target.value })
                        }
                        disabled={isSavingEdit}
                      />
                    </label>
                  ) : (
                    <label style={{ width: 160 }}>
                      <span className="label">Target Time</span>
                      <input
                        type="text"
                        className="input"
                        placeholder="6:00"
                        value={editForm.targetTime}
                        onChange={(e) =>
                          updateEditForm(workout.id, { targetTime: e.target.value })
                        }
                        disabled={isSavingEdit}
                      />
                    </label>
                  )}
                </div>

                <label style={{ display: "block", marginTop: 12 }}>
                  <span className="label">Notes</span>
                  <textarea
                    className="input"
                    rows={3}
                    value={editForm.notes}
                    onChange={(e) => updateEditForm(workout.id, { notes: e.target.value })}
                    disabled={isSavingEdit}
                  />
                </label>

                <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="btn primary tiny"
                    disabled={isSavingEdit}
                    onClick={(e) => {
                      e.stopPropagation();
                      saveWorkoutDetails(workout);
                    }}
                  >
                    <FaSave /> Save
                  </button>

                </div>
              </div>
            ) : (
              <button
                type="button"
                className="btn ghost tiny"
                onClick={(e) => {
                  e.stopPropagation();
                  startEditingWorkout(workout);
                }}
              >
                Edit Details
              </button>
            )}
          </div>
        )}

        {isExpanded && workout.notes && !isEditing && (
          <p style={{ marginTop: 8, fontSize: 13, color: "#475569" }}>{workout.notes}</p>
        )}

        {isExpanded && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, alignItems: "center" }}>
            {pieces.length > 0 && (
              <>
                {pieces.map((piece) => (
                  <button
                    key={piece.id}
                    type="button"
                    className={`btn ghost tiny ${!showingAverages && activePiece && activePiece.id === piece.id ? "active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActivePieceTabs((prev) => ({ ...prev, [workout.id]: piece.id }));
                    }}
                  >
                    Piece {piece.pieceNumber}
                  </button>
                ))}
                <button
                  type="button"
                  className={`btn ghost tiny ${showingAverages ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePieceTabs((prev) => ({ ...prev, [workout.id]: AVERAGES_TAB }));
                  }}
                >
                  Averages
                </button>
              </>
            )}
            {allowEdits && (
              <button
                type="button"
                className="btn ghost tiny"
                disabled={isAddingPiece}
                onClick={(e) => {
                  e.stopPropagation();
                  addPieceToWorkout(workout);
                }}
              >
                <FaPlus /> Add Piece
              </button>
            )}
          </div>
        )}

        {isExpanded && showingAverages && renderWorkoutAverages(workout)}

        {isExpanded && !showingAverages && !activePiece && (
          <p className="help" style={{ marginTop: 8 }}>This workout does not have any pieces yet.</p>
        )}

        {isExpanded && !showingAverages && activePiece && (
          <div style={{ marginTop: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
                gap: 8,
              }}
            >
              <div style={{ fontWeight: 600 }}>
                Piece {activePiece.pieceNumber} –{" "}
                {measurementForPiece(workout, activePiece) === "distance"
                  ? `${activePiece.targetDistanceMeters ?? workout.targetDistanceMeters ?? ""}m`
                  : formatSeconds(activePiece.targetTimeSeconds ?? workout.targetTimeSeconds)}
              </div>
              {allowEdits && (
                <button
                  type="button"
                  className="btn ghost tiny"
                  disabled={deletingPieceId === activePiece.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    deletePieceFromWorkout(workout, activePiece);
                  }}
                >
                  <FaTrash /> Delete Piece
                </button>
              )}
            </div>

            {allowEdits
              ? renderPieceGrid(workout, activePiece)
              : renderPieceResultsReadOnly(workout, activePiece)}
          </div>
        )}
      </div>
    );
  }

  function renderAthleteMatrix() {
    const hasBaseColumns = athleteMatrix.columns.length > 0;
    const hasOyoColumn = includeOyo;
    if (!hasBaseColumns && !hasOyoColumn) {
      return (
        <div className="card" style={{ padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Athlete Averages</h3>
          <p className="help">No workout data available to summarize.</p>
        </div>
      );
    }
    const columns = includeOyo
      ? [
          ...athleteMatrix.columns,
          {
            id: "__oyo__",
            label: "OYO",
            isOyo: true,
          },
        ]
      : athleteMatrix.columns;
    const rowsMap = new Map();
    // Start with base workout rows (team results)
    athleteMatrix.rows.forEach((row) => {
      rowsMap.set(String(row.athleteId), { ...row });
    });
    // Ensure all roster athletes are present when showing OYO
    if (includeOyo && roster.length) {
      roster.forEach((athlete) => {
        const key = String(athlete.id || athlete.athleteId);
        if (!rowsMap.has(key)) {
          rowsMap.set(key, {
            athleteId: Number(athlete.id || athlete.athleteId),
            name: athlete.name || `Athlete ${athlete.id || athlete.athleteId}`,
            values: {},
          });
        }
      });
    }
    // Include OYO-only athletes even if not in roster
    if (includeOyo) {
      Object.keys(athleteOyoLatest).forEach((athleteId) => {
        const key = String(athleteId);
        if (!rowsMap.has(key)) {
          rowsMap.set(key, {
            athleteId: Number(athleteId),
            name:
              roster.find((a) => String(a.id) === key)?.name ||
              athleteOyoLatest[athleteId]?.athleteName ||
              `Athlete ${athleteId}`,
            values: {},
          });
        }
      });
    }
    const rows = Array.from(rowsMap.values()).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    );

    return (
      <div className="card attendance-card">
        <h3 style={{ marginTop: 0, marginLeft: 16, marginRight: 16, paddingTop: 16 }}>Athlete Averages</h3>
        <div className="attendance-table-wrapper">
          <div className="attendance-grid">
            <div className="attendance-row attendance-header-row">
              <div className="attendance-name-col">Athlete</div>
              {columns.map((column) => {
                if (column.id === "__oyo__") {
                  return (
                    <div key={column.id} className="attendance-practice-col" title="OYO Workouts">
                      <div className="practice-label-date">OYO</div>
                      <div className="practice-label-day">Latest</div>
                    </div>
                  );
                }
                const workout = workouts.find((w) => String(w.id) === column.id);
                const dateLabel = workout?.practiceDate
                  ? new Date(`${workout.practiceDate}T00:00:00`).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })
                  : "";
                return (
                  <div key={column.id} className="attendance-practice-col" title={column.label}>
                    <div className="practice-label-date">{dateLabel}</div>
                    <div className="practice-label-day">{workout?.name || "Workout"}</div>
                  </div>
                );
              })}
            </div>
            {rows.map((row) => (
              <div key={row.athleteId} className="attendance-row">
                <div className="attendance-name-col">
                  <div className="roster-name">{row.name}</div>
                </div>
                {columns.map((column) => {
                  if (column.id === "__oyo__") {
                    const summary = athleteOyoLatest[String(row.athleteId)];
                    return (
                      <div key={`${row.athleteId}-oyo`} className="attendance-practice-col">
                        {summary ? (
                          <>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                              {summary.description || "Logged"}
                            </div>
                            <div className="help" style={{ fontSize: 11 }}>
                              {formatEntryDate(summary.performedOn)}
                            </div>
                          </>
                        ) : (
                          "—"
                        )}
                      </div>
                    );
                  }
                  const stat = row.values[column.id];
                  if (!stat) {
                    return <div key={column.id} className="attendance-practice-col">—</div>;
                  }
                  const measurement = column.measurement || "distance";
                  let resultLabel = "—";
                  if (measurement === "distance") {
                    resultLabel =
                      stat.avgTimeSeconds != null
                        ? formatSeconds(stat.avgTimeSeconds)
                        : "—";
                  } else {
                    resultLabel =
                      stat.avgDistanceMeters != null
                        ? `${Math.round(stat.avgDistanceMeters)} m`
                        : "—";
                  }
                  const splitLabel =
                    stat.avgSplitSeconds != null
                      ? formatSeconds(stat.avgSplitSeconds)
                      : null;
                  const wattsLabel =
                    stat.avgWatts != null ? Math.round(stat.avgWatts) : null;
                  return (
                    <div key={column.id} className="attendance-practice-col">
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{resultLabel}</div>
                      {splitLabel && (
                        <div className="help" style={{ fontSize: 11 }}>Split {splitLabel}</div>
                      )}
                      {wattsLabel != null && (
                        <div className="help" style={{ fontSize: 11 }}>{wattsLabel}w</div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function renderIndividualView() {
    return (
      <div className="card" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Individual Athlete</h3>
        <label style={{ display: "block", marginBottom: 12 }}>
          <span className="label">Athlete</span>
          <select
            className="input"
            value={individualAthleteId}
            onChange={(e) => setIndividualAthleteId(e.target.value)}
          >
            <option value="">Select an athlete</option>
            {roster.filter((a) => a.role_key === "athlete").map((athlete) => (
              <option key={athlete.id} value={athlete.id}>
                {athlete.name || `Athlete ${athlete.id}`}
              </option>
            ))}
          </select>
        </label>
        {individualError && (
          <p className="help" style={{ color: "red" }}>{individualError}</p>
        )}
        {!individualAthleteId && (
          <p className="help">Choose an athlete to view their workouts.</p>
        )}
        {individualAthleteId && individualLoading && (
          <p className="help">Loading workout history…</p>
        )}
        {individualAthleteId && !individualLoading && individualEntries.length === 0 && (
          <p className="help">No workouts recorded for this athlete yet.</p>
        )}
        {individualEntries.length > 0 && (
          <div className="stack" style={{ gap: 12 }}>
            {individualEntries.map((entry) => {
              const isTeam = entry.entryType === "team";
              return (
                  <div
                    key={entry.id || `${entry.entryType}-${entry.performedOn}-${entry.description}`}
                    className={`oyoEntry ${isTeam ? "teamWorkout" : ""}`}
                    style={{ border: "1px solid var(--border)", cursor: !isTeam && entry.oyoId ? "pointer" : "default" }}
                    onClick={() => {
                      if (!isTeam && entry.oyoId) {
                        setOyoWorkoutModalId(entry.oyoId);
                      }
                    }}
                  >
                    <div className="oyoEntryHeader">
                    <div>
                    <div className="oyoEntryDate">{formatEntryDate(entry.performedOn)}</div>
                      <div className="oyoEntryDescription">
                        {entry.description || (isTeam ? "Team workout" : "OYO workout")}
                      </div>
                    </div>
                    <div className="oyoEntryActions">
                      <span className={`tag ${isTeam ? "published" : "info"}`}>
                        {isTeam ? "Team" : "OYO"}
                      </span>
                    </div>
                  </div>
                  {isTeam ? (
                    <div className="oyoEntryMeta teamMeta">
                      <span><strong>Result:</strong> {formatTeamResult(entry)}</span>
                      {entry.targetDistanceMeters != null && (
                        <span><strong>Target:</strong> {`${entry.targetDistanceMeters} m`}</span>
                      )}
                      {entry.targetTimeSeconds != null && (
                        <span><strong>Target:</strong> {formatSeconds(entry.targetTimeSeconds)}</span>
                      )}
                      {entry.splitSeconds != null && (
                        <span><strong>Split:</strong> {formatSeconds(entry.splitSeconds)}</span>
                      )}
                      {entry.watts != null && (
                        <span><strong>Watts:</strong> {entry.watts}</span>
                      )}
                      {entry.practiceTime && (
                        <span><strong>Time:</strong> {entry.practiceTime}</span>
                      )}
                      <span className="help" style={{ margin: 0 }}>
                        Logged {formatRelative(entry.recordedAt || entry.createdAt)}
                      </span>
                    </div>
                  ) : (
                    <div className="oyoEntryMeta">
                      {entry.durationMinutes && <span>{entry.durationMinutes} min</span>}
                      {entry.distanceMeters && <span>{entry.distanceMeters} m</span>}
                      {entry.notes && <span>{entry.notes}</span>}
                      <span className="help" style={{ margin: 0 }}>
                        Logged {formatRelative(entry.createdAt)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ marginTop: 0, marginBottom: 12 }}>Workout Tracker</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className={`btn ghost view-toggle ${viewMode === "workouts" ? "active" : ""}`}
              onClick={() => setViewMode("workouts")}
            >
              By Workout
            </button>
            <button
              type="button"
              className={`btn ghost view-toggle ${viewMode === "athletes" ? "active" : ""}`}
              onClick={() => setViewMode("athletes")}
              disabled={!workouts.length}
            >
              By Athlete
            </button>
            <button
              type="button"
              className={`btn ghost view-toggle ${viewMode === "individual" ? "active" : ""}`}
              onClick={() => setViewMode("individual")}
              disabled={!roster.length}
            >
              Individual
            </button>
          </div>
          {allowEdits && (
            <button
              className="btn primary"
              type="button"
              disabled={!practices.length}
              onClick={() => setShowCreateModal(true)}
            >
              <FaPlus /> Add Workout
            </button>
          )}
        </div>
        <label style={{ display: "block", marginBottom: 8 }}>
          <span className="label" style={{ WebkitTextSizeAdjust: "100%", textSizeAdjust: "100%" }}>
            Filter by Practice
          </span>
          <select
            className="input"
            value={selectedPracticeId || ""}
            onChange={(e) => setSelectedPracticeId(e.target.value)}
          >
            <option value="">Select a practice</option>
            {practiceOptions}
          </select>
        </label>
        {selectedPractice && (
          <p className="help" style={{ marginTop: 0, marginBottom: 8 }}>
            {formatPracticeLabel(selectedPractice)}
          </p>
        )}
        {showingAllPractices && (
          <p className="help" style={{ marginTop: 0, marginBottom: 8 }}>
            Showing workouts for all practices in this range.
          </p>
        )}
        {selectedPracticeId && selectedPracticeId !== ALL_PRACTICES_OPTION && (
          <label style={{ display: "block", marginBottom: 8 }}>
            <span className="label" style={{ WebkitTextSizeAdjust: "100%", textSizeAdjust: "100%" }}>
              Filter Athletes by Attendance
            </span>
            <select
              className="input"
              value={attendanceFilter}
              disabled={
                attendanceLoading ||
                !Object.prototype.hasOwnProperty.call(attendanceByPractice, selectedPracticeId)
              }
              onChange={(e) => setAttendanceFilter(e.target.value)}
            >
              <option value="all">All athletes</option>
              <option value="available">Available</option>
              <option value="unavailable">Unavailable</option>
              <option value="no_response">No Response</option>
            </select>
          </label>
        )}
        {loadingPractices && <p className="help" style={{ marginTop: 8 }}>Loading practices…</p>}
        {practiceError && (
          <p className="help" style={{ marginTop: 8, color: "red" }}>{practiceError}</p>
        )}
        {attendanceLoading && (
          <p className="help" style={{ marginTop: 0 }}>Loading attendance…</p>
        )}
        {attendanceError && (
          <p className="help" style={{ marginTop: 0, color: "red" }}>{attendanceError}</p>
        )}
      </div>

      {viewMode === "workouts" ? (
        <>
          {!selectedPracticeId && (
            <div className="card" style={{ padding: 16 }}>
              <p className="help">Select a practice to view workouts.</p>
            </div>
          )}
          {workoutsLoading && (
            <div className="card" style={{ padding: 16 }}>
              <p className="help">Loading workouts…</p>
            </div>
          )}
          {workoutsError && (
            <div className="card" style={{ padding: 16 }}>
              <p className="help" style={{ color: "red" }}>{workoutsError}</p>
            </div>
          )}
          {rosterError && (
            <div className="card" style={{ padding: 16 }}>
              <p className="help" style={{ color: "red" }}>{rosterError}</p>
            </div>
          )}
          {!workoutsLoading && selectedPracticeId && !workouts.length && (
            <div className="card" style={{ padding: 16 }}>
              <p className="help">No workouts for this practice yet.</p>
            </div>
          )}
          {workouts.map((workout) => renderWorkout(workout))}
        </>
      ) : viewMode === "athletes" ? (
        renderAthleteMatrix()
      ) : (
        renderIndividualView()
      )}

      {allowEdits && showCreateModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="card"
            style={{
              width: "100%",
              maxWidth: 640,
              maxHeight: "90vh",
              overflowY: "auto",
              padding: 24,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Add Workout</h3>
              <button
                className="btn ghost tiny"
                type="button"
                onClick={() => setShowCreateModal(false)}
              >
                Close
              </button>
            </div>
            <form className="stack" style={{ gap: 12 }} onSubmit={handleCreateWorkout}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                <label style={{ flex: "1 1 180px" }}>
                  <span className="label">Name</span>
                  <input
                    className="input"
                    value={createForm.name}
                    onChange={(e) => handleFormChange("name", e.target.value)}
                    placeholder="Morning Erg"
                  />
                </label>
                <label style={{ flex: "1 1 220px" }}>
                  <span className="label">Practice</span>
                  <select
                    className="input"
                    value={createForm.practiceId}
                    onChange={(e) => handleFormChange("practiceId", e.target.value)}
                  >
                    <option value="">Select a practice</option>
                    {practices.map((practice) => (
                      <option key={practice.id} value={practice.id}>
                        {formatPracticeLabel(practice)}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ width: 160 }}>
                  <span className="label">Type</span>
                  <select
                    className="input"
                    value={createForm.workoutType}
                    onChange={(e) => handleFormChange("workoutType", e.target.value)}
                  >
                    <option value="erg">Erg</option>
                    <option value="water">Water</option>
                  </select>
                </label>
                <label style={{ width: 160 }}>
                  <span className="label">Team</span>
                  <select
                    className="input"
                    value={createForm.team}
                    onChange={(e) => handleFormChange("team", e.target.value)}
                  >
                    <option value="">All Teams</option>
                    {teams.map((team) => (
                      <option key={team.id || team.name} value={team.name}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ width: 160 }}>
                  <span className="label">Squad</span>
                  <select
                    className="input"
                    value={createForm.squadId}
                    onChange={(e) => handleSquadChange(e.target.value)}
                  >
                    <option value="">All Squads</option>
                    {squads.map((squad) => (
                      <option key={squad.id} value={squad.id}>
                        {squad.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ width: 160 }}>
                  <span className="label">Age Group</span>
                  <select
                    className="input"
                    value={createForm.ageGroup}
                    onChange={(e) => handleFormChange("ageGroup", e.target.value)}
                  >
                    <option value="">All</option>
                    {ageGroupOptions.map((group) => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                <label style={{ width: 160 }}>
                  <span className="label">Pieces</span>
                  <input
                    type="number"
                    min="1"
                    className="input"
                    value={createForm.pieceCount}
                    onChange={(e) => handleFormChange("pieceCount", e.target.value)}
                  />
                </label>
                <label style={{ width: 160 }}>
                  <span className="label">Measure By</span>
                  <select
                    className="input"
                    value={createForm.measurementType}
                    onChange={(e) => handleFormChange("measurementType", e.target.value)}
                  >
                    <option value="distance">Distance</option>
                    <option value="time">Time</option>
                  </select>
                </label>
                {createForm.measurementType === "distance" ? (
                  <label style={{ width: 160 }}>
                    <span className="label">Distance (m)</span>
                    <input
                      type="number"
                      className="input"
                      value={createForm.targetDistanceMeters}
                      onChange={(e) => handleFormChange("targetDistanceMeters", e.target.value)}
                    />
                  </label>
                ) : (
                  <label style={{ width: 160 }}>
                    <span className="label">Target Time</span>
                    <input
                      type="text"
                      className="input"
                      placeholder=""
                      value={createForm.targetTime}
                      onChange={(e) => handleFormChange("targetTime", e.target.value)}
                    />
                  </label>
                )}
              </div>

              <label>
                <span className="label">Notes</span>
                <textarea
                  className="input"
                  rows={3}
                  value={createForm.notes}
                  onChange={(e) => handleFormChange("notes", e.target.value)}
                />
              </label>

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="submit" className="btn primary" disabled={creatingWorkout}>
                   Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {oyoWorkoutModalId && (
        <OyoWorkoutModal workoutId={oyoWorkoutModalId} onClose={() => setOyoWorkoutModalId(null)} />
      )}
    </div>
  );
}
